import { Dispatch, SetStateAction } from 'react';

import Paper from '@material-ui/core/Paper';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Box } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { ALM_KIND_SET, ALM_SMM_DAT } from '../../molecules/Grid/AlarmGrid';
import AlarmKindDiv from '../../molecules/Div/AlarmKindDiv';
import AlarmStateDiv from '../../molecules/Div/AlarmStateDiv';
import DateTImeDiv from '../../atoms/Div/DateTImeDiv';
import { useTranslation } from 'react-i18next';

export type AlarmSummaryGridProps = {
  alarmContainerStyle: string;
  alarmArray?: AlarmDatas;
  selectIndex: number;
  setSelectIndex: Dispatch<SetStateAction<number>>;
};

export type AlarmDatas = Array<{
  ALM_SMM_DAT: ALM_SMM_DAT;
  ALM_KIND_SET: ALM_KIND_SET;
  rowno: number;
}>;

const useStyles = makeStyles(
  createStyles({
    '@global': {
      '.MuiTableCell-body': { color: 'inherit' },
    },
    //テーブル外枠
    tableStyle: {
      borderWidth: '1px',
      borderColor: '#dcdcdc',
      borderStyle: 'solid',
    },
    tableHeader: {
      borderWidth: '1px',
      borderColor: '#dcdcdc',
      borderStyle: 'solid',
      backgroundColor: 'grey',
      textAlign: 'center',
      color: 'white',
      padding: '1px',
      minWidth: '80px',
      borderCollapse: `collapse`,
    },
    tableItem: {
      borderWidth: '1px',
      borderColor: '#dcdcdc',
      orderBottomStyle: 'inset',
      borderLeftStyle: 'solid',
      borderRightStyle: 'solid',
      textAlign: 'center',
      minWidth: '80px',
      height: '8px',
      padding: '1px',
    },
    tableItemSub: {
      borderWidth: '1px',
      borderColor: '#dcdcdc',
      borderLeftStyle: 'solid',
      borderRightStyle: 'solid',
      textAlign: 'center',
      verticalAlign: 'middle',
      width: '50%',
      minWidth: '80px',
      height: '100%',
      padding: '1px',
    },
    alarmValue: {
      textAlign: 'center',
      padding: '0px',
      verticalAlign: 'bottom',
    },
    cellNo: {
      width: '30px',
    },
    cellDateTime: {
      width: '180px',
    },
    cellKubun: {
      width: '140px',
    },
    cellDeviceNo: {
      width: '100px',
    },
    cellDeviceName: {
      width: '160px',
    },
    cellItemName: {
      width: '160px',
    },
    cellAlertType: {
      width: '220px',
    },
    cellMessage: {
      width: '420px',
    },
    cellStatus: {
      width: '100px',
    },
    cellAlarm: {
      width: '140px',
    },
    cellAlarmSub: {
      height: '29px',
      paddingLeft: '1em',
    },
    alertRoll: {
      overflow: 'auto',
    },
    isNormal: {
      color: 'green',
    },
    isNormalNC: {
      color: 'green',
      background: '#c0ffc0',
    },
    isNormalWS: {
      color: 'green',
      background: '#7fffd4',
    },
    hasAlert: {
      color: 'red',
    },
    hasAlertLFault: {
      color: '#FFA50A',
    },
    hasAlertHFault: {
      color: 'red',
    },
    hasAlertSFault: {
      color: 'red',
    },
    hasAlertNC: {
      color: 'red',
      background: '#ffc0c0', //淡い赤系
    },
    hasAlertNCLFault: {
      //軽故障
      color: '#FFA50A', //オレンジ色
      background: '#ffc0c0',
    },
    hasAlertNCHFault: {
      //重故障
      color: 'red',
      background: '#ffc0c0',
    },
    hasAlertNCSFault: {
      //システム異常
      color: 'red',
      background: '#ffc0c0',
    },
    hasAlertWS: {
      color: 'red',
      background: '#7fffd4', //あざやかな青緑系
    },
    hasAlertWSLFault: {
      //軽故障
      color: '#FFA50A', //オレンジ色
      background: '#7fffd4',
    },
    hasAlertWSHFault: {
      //重故障
      color: 'red',
      background: '#7fffd4',
    },
    hasAlertWSSFault: {
      //システム異常
      color: 'red',
      background: '#7fffd4',
    },
  })
);

/**
 * 状態が復帰の場合メッセージを空白にする
 * @param data
 * @returns
 */
const getAlarmMessage = (data: ALM_SMM_DAT): string => {
  if (data.STAT === 2) {
    return '';
  }
  return data.MESSAGE;
};
//少数第3位出力
export const alertValueFormat = (val: Number): string => {
  //console.debug(['val', val, 'val  && !Number.isNaN(val) ?', val != null && !Number.isNaN(val)]);
  if (val != null && !Number.isNaN(val)) {
    return val.toFixed(4);
  }
  return '-';
};

const AlarmSummaryGrid = (props: AlarmSummaryGridProps) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const selectRow = (idx: number) => {
    props.setSelectIndex(idx);
  };

  return (
    <>
      <TableContainer component={Paper} className={props.alarmContainerStyle}>
        <Table aria-label="alarm summary" stickyHeader className={classes.tableStyle}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader + ' ' + classes.cellNo}>
                {t('AlarmSummaryGrid.No', 'No.')}
              </TableCell>
              <TableCell className={classes.tableHeader + ' ' + classes.cellDateTime}>
                {t('AlarmSummaryGrid.日時', '日時')}
              </TableCell>
              <TableCell className={classes.tableHeader + ' ' + classes.cellKubun}>
                {t('AlarmSummaryGrid.区分', '区分')}
              </TableCell>
              <TableCell className={classes.tableHeader + ' ' + classes.cellDeviceNo}>
                {t('AlarmSummaryGrid.機器番号', '機器番号')}
              </TableCell>
              <TableCell className={classes.tableHeader + ' ' + classes.cellDeviceName}>
                {t('AlarmSummaryGrid.機器名称', '機器名称')}
              </TableCell>
              <TableCell className={classes.tableHeader + ' ' + classes.cellItemName}>
                {t('AlarmSummaryGrid.項目名称', '項目名称')}
              </TableCell>
              <TableCell className={classes.tableHeader + ' ' + classes.cellAlertType}>
                {t('AlarmSummaryGrid.異常種別', '異常種別')}
              </TableCell>
              <TableCell className={classes.tableHeader + ' ' + classes.cellMessage}>
                {t('AlarmSummaryGrid.メッセージ', 'メッセージ')}
              </TableCell>
              <TableCell className={classes.tableHeader + ' ' + classes.cellStatus}>
                {t('AlarmSummaryGrid.状態', '状態')}
              </TableCell>
              <TableCell className={classes.tableHeader}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div className={classes.tableHeader}>{t('AlarmSummaryGrid.警報', '警報')}</div>
                  <div style={{ display: 'flex' }}>
                    <div style={{ width: '50%' }} className={classes.tableHeader}>
                      {t('AlarmSummaryGrid.検出値', '検出値')}
                    </div>
                    <div style={{ width: '50%' }} className={classes.tableHeader}>
                      {t('AlarmSummaryGrid.閾値', '閾値')}
                    </div>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {props.alarmArray?.map((item) => {
              let lineAttr = '';
              if (item.ALM_SMM_DAT.STAT === 1) {
                //検出
                if (props.selectIndex === item.rowno) {
                  //各異常内容ごとに色変更
                  if (item.ALM_SMM_DAT.ALM_KIND == 1) {
                    lineAttr = classes.hasAlertWSHFault;
                  } else if (item.ALM_SMM_DAT.ALM_KIND == 2) {
                    lineAttr = classes.hasAlertWSLFault;
                  } else if (item.ALM_SMM_DAT.ALM_KIND == 3) {
                    lineAttr = classes.hasAlertWSSFault;
                  } else {
                    lineAttr = classes.hasAlertWS;
                  }
                } else if (!item.ALM_SMM_DAT.CHECKED) {
                  //各異常内容ごとに色変更
                  if (item.ALM_SMM_DAT.ALM_KIND == 1) {
                    lineAttr = classes.hasAlertNCHFault;
                  } else if (item.ALM_SMM_DAT.ALM_KIND == 2) {
                    lineAttr = classes.hasAlertNCLFault;
                  } else if (item.ALM_SMM_DAT.ALM_KIND == 3) {
                    lineAttr = classes.hasAlertNCSFault;
                  } else {
                    lineAttr = classes.hasAlertNC;
                  }
                } else {
                  if (item.ALM_SMM_DAT.ALM_KIND == 1) {
                    lineAttr = classes.hasAlertHFault;
                  } else if (item.ALM_SMM_DAT.ALM_KIND == 2) {
                    lineAttr = classes.hasAlertLFault;
                  } else if (item.ALM_SMM_DAT.ALM_KIND == 3) {
                    lineAttr = classes.hasAlertSFault;
                  } else {
                    lineAttr = classes.hasAlert;
                  }
                }
              } else {
                //復帰
                if (props.selectIndex === item.rowno) {
                  lineAttr = classes.isNormalWS;
                } else if (!item.ALM_SMM_DAT.CHECKED) {
                  lineAttr = classes.isNormalNC;
                } else {
                  lineAttr = classes.isNormal;
                }
              }

              return (
                <TableRow className={lineAttr}>
                  <TableCell className={classes.tableItem} onClick={(e) => selectRow(item.rowno)}>
                    {item.rowno}
                  </TableCell>
                  <TableCell className={classes.tableItem} onClick={(e) => selectRow(item.rowno)}>
                    <DateTImeDiv value={item.ALM_SMM_DAT.COL_DATE} format={'YY/MM/DD HH:mm:ss'} />
                  </TableCell>
                  <TableCell className={classes.tableItem} onClick={(e) => selectRow(item.rowno)}>
                    <AlarmKindDiv kind={item.ALM_SMM_DAT.ALM_KIND} />
                  </TableCell>
                  <TableCell className={classes.tableItem} onClick={(e) => selectRow(item.rowno)}>
                    {item.ALM_SMM_DAT.DEV_NO}
                  </TableCell>
                  <TableCell className={classes.tableItem} onClick={(e) => selectRow(item.rowno)}>
                    {item.ALM_SMM_DAT.DEV_NAME}
                  </TableCell>
                  <TableCell className={classes.tableItem} onClick={(e) => selectRow(item.rowno)}>
                    {item.ALM_SMM_DAT.ITM_NAME}
                  </TableCell>
                  <TableCell className={classes.tableItem} onClick={(e) => selectRow(item.rowno)}>
                    {item.ALM_KIND_SET?.TYP_NAME}
                  </TableCell>
                  <TableCell className={classes.tableItem} onClick={(e) => selectRow(item.rowno)}>
                    {getAlarmMessage(item.ALM_SMM_DAT)}
                  </TableCell>
                  <TableCell className={classes.tableItem} onClick={(e) => selectRow(item.rowno)}>
                    <AlarmStateDiv state={item.ALM_SMM_DAT.STAT} devName={item.ALM_SMM_DAT.DEV_NAME} />
                  </TableCell>
                  {/* 警報 */}
                  <TableCell className={classes.tableItem} onClick={(e) => selectRow(item.rowno)}>
                    <div style={{ display: 'flex', height: '100%' }}>
                      <div className={classes.tableItemSub}>{alertValueFormat(item.ALM_SMM_DAT.DATA)}</div>
                      <div className={classes.tableItemSub}>{alertValueFormat(item.ALM_SMM_DAT.LIMIT)}</div>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default AlarmSummaryGrid;
